import { useEffect, useState } from 'react';
import {
  Box, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerFooter, DrawerHeader, DrawerOverlay, Flex, Grid, Spinner, Text,
} from '@chakra-ui/react';
import { getMovementHistory, errorMessage, formatDay, formatMoney } from 'api/administrador/finance';

const FIELD_LABEL = {
  date: 'Fecha', type: 'Tipo', amount: 'Monto', trm: 'TRM', concept: 'Concepto', third_party: 'Tercero',
  student_code: 'Código', note: 'Nota', categoria: 'Categoría', revision: 'Revisión', comentario: 'Comentario',
  anulado: 'Anulado', motivo: 'Motivo',
};

const show = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));

function changes(prev, next) {
  if (!prev || !next) return [];
  return Object.keys({ ...prev, ...next })
    .filter((k) => JSON.stringify(prev[k]) !== JSON.stringify(next[k]))
    .map((k) => ({ field: FIELD_LABEL[k] || k, before: show(prev[k]), after: show(next[k]) }));
}

const initials = (name) => String(name || '?').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

/** Historial de un movimiento: sale de Auditoría (audit-log, entity_type finance-movement). */
export default function HistoryDrawer({ movementId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!movementId) return;
    setData(null);
    setError(null);
    getMovementHistory(movementId).then(setData).catch((e) => setError(errorMessage(e)));
  }, [movementId]);

  const m = data?.movement;
  return (
    <Drawer isOpen={!!movementId} onClose={onClose} placement="right" size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          <Text fontSize="xl" fontWeight="600">Historial del movimiento</Text>
          {m && <Text fontSize="sm" color="gray.500" fontWeight="400">{m.concept} · {formatDay(m.date)} · {formatMoney(m.amount)}</Text>}
        </DrawerHeader>
        <DrawerBody py={5}>
          {error && <Text color="red.600" fontSize="sm">{error}</Text>}
          {!data && !error && <Flex justify="center" py={8}><Spinner /></Flex>}
          {data?.events?.length === 0 && <Text fontSize="sm" color="gray.500">Sin cambios registrados.</Text>}
          {data?.events?.map((e, i) => {
            const diff = changes(e.prev, e.next);
            return (
              <Grid key={e.id} templateColumns="28px 1fr" gap={3}>
                <Flex direction="column" align="center">
                  <Flex w="28px" h="28px" borderRadius="full" bg="purple.100" color="purple.800" align="center" justify="center" fontSize="xs" fontWeight="600">
                    {e.who ? initials(e.who) : 'XL'}
                  </Flex>
                  {i < data.events.length - 1 && <Box flex={1} w="2px" bg="gray.100" my={1} />}
                </Flex>
                <Box pb={5} minW={0}>
                  <Text fontSize="sm"><b>{e.who || 'Migración'}</b> · {e.summary}</Text>
                  <Text fontSize="xs" color="gray.500" mt={0.5}>{e.at ? new Date(e.at).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' }) : ''}</Text>
                  {diff.length > 0 && (
                    <Grid mt={2} templateColumns="auto 1fr" gap="4px 12px" fontSize="13px" p={2.5} bg="gray.50" border="1px solid" borderColor="gray.100" borderRadius="md">
                      {diff.map((d) => (
                        <Box key={d.field} display="contents">
                          <Text color="gray.500">{d.field}</Text>
                          <Text wordBreak="break-word"><Text as="span" textDecoration="line-through" color="gray.400">{d.before}</Text> → <b>{d.after}</b></Text>
                        </Box>
                      ))}
                    </Grid>
                  )}
                </Box>
              </Grid>
            );
          })}
        </DrawerBody>
        <DrawerFooter borderTopWidth="1px" justifyContent="flex-start">
          <Text fontSize="sm" color="gray.500">Sale de Auditoría. El superadmin ve todos los cambios en el menú Auditoría.</Text>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
